import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useCart, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import MoleculeLoader from '../components/MoleculeLoader';
import { CheckCircle2, Package, ArrowRight, XCircle, Clock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccessPage = () => {
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const { token } = useAuth();
  const [status, setStatus] = useState('checking');
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [settings, setSettings] = useState(null);

  // Load site settings for Zelle / Cash App payment instructions shown below.
  useEffect(() => {
    let cancelled = false;
    fetch(`${API_URL}/api/site-settings`)
      .then((r) => (r.ok ? r.json() : null))
      .then((s) => { if (!cancelled && s) setSettings(s); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // PayPal + Cash flow both arrive with ?free_order=ORDER_ID. Stripe was
  // removed Apr 28, 2026, so session_id polling is no longer used.
  const orderId = searchParams.get('free_order') || searchParams.get('order_id');
  const legacySessionId = searchParams.get('session_id');

  // Refs for unstable values (clearCart from CartProvider is recreated every render
  // of its parent; including it in deps caused an infinite "Confirming your order…"
  // loop where each fetch was cancelled before its setState landed).
  const clearCartRef = React.useRef(clearCart);
  React.useEffect(() => { clearCartRef.current = clearCart; }, [clearCart]);
  const tokenRef = React.useRef(token);
  React.useEffect(() => { tokenRef.current = token; }, [token]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      // Always clear the cart on success page (idempotent; harmless if already empty).
      try { clearCartRef.current && clearCartRef.current(); } catch (e) { console.error('Cart clear failed:', e); }

      if (legacySessionId && !orderId) {
        // Old Stripe deep-links — show a helpful message instead of polling a dead endpoint.
        if (!cancelled) setStatus('legacy');
        return;
      }
      if (!orderId) {
        if (!cancelled) setStatus('error');
        return;
      }
      // Fetch the order from our own API to display total + status accurately.
      try {
        const res = await fetch(`${API_URL}/api/orders/${orderId}`, {
          headers: tokenRef.current ? { Authorization: `Bearer ${tokenRef.current}` } : {},
        });
        if (res.ok) {
          const order = await res.json();
          if (!cancelled) {
            setPaymentDetails({
              order_id: order.order_id,
              total: order.total,
              currency: order.currency || 'usd',
              payment_status: order.payment_status || (order.payment_method === 'cash' ? 'pending' : 'paid'),
              payment_method: order.payment_method,
              is_preorder: order.is_preorder,
              delivery_method: order.delivery_method,
              pickup_date: order.pickup_date,
              pickup_time: order.pickup_time,
            });
            setStatus('success');
          }
          return;
        }
        // 404/401 fallback — still show success with order_id
        if (!cancelled) {
          setPaymentDetails({ order_id: orderId, payment_status: 'paid' });
          setStatus('success');
        }
      } catch (e) {
        if (!cancelled) {
          setPaymentDetails({ order_id: orderId, payment_status: 'paid' });
          setStatus('success');
        }
      }
    };
    load();
    // Safety net: even if fetch hangs, never leave the user on the spinner forever.
    const failsafe = setTimeout(() => {
      if (!cancelled) {
        setPaymentDetails(prev => prev || { order_id: orderId, payment_status: 'paid' });
        setStatus(prev => (prev === 'checking' ? (orderId ? 'success' : 'error') : prev));
      }
    }, 8000);
    return () => { cancelled = true; clearTimeout(failsafe); };
    // Intentionally only re-run when the URL params change. Functions/token are
    // read via refs above to avoid the infinite re-mount loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId, legacySessionId]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <MoleculeLoader size={96} label="Confirming your order — just a moment…" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error' || status === 'legacy') {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mx-auto mb-6">
              <XCircle className="h-8 w-8 text-amber-600" />
            </div>
            <h1 className="font-heading text-xl font-bold text-slate-900 mb-2">
              {status === 'legacy' ? 'Order Already Processed' : 'No Order Found'}
            </h1>
            <p className="text-slate-600 mb-6">
              {status === 'legacy'
                ? 'This was an older checkout link. If you placed an order, it should appear in your dashboard. A confirmation email was also sent.'
                : 'We couldn’t find an order to display here. If you just paid, please check your email — your order may already be confirmed.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/dashboard">
                <Button className="bg-teal-700 hover:bg-teal-800 text-white w-full sm:w-auto" data-testid="success-view-orders-btn">
                  View My Orders
                </Button>
              </Link>
              <Link to="/products">
                <Button variant="outline" className="w-full sm:w-auto">Continue Shopping</Button>
              </Link>
              <Link to="/contact">
                <Button variant="ghost" className="w-full sm:w-auto">Contact Support</Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // SUCCESS view
  const isCash = paymentDetails?.payment_method === 'cash';
  const isZelle = paymentDetails?.payment_method === 'zelle';
  const isCashApp = paymentDetails?.payment_method === 'cash_app';
  const isManualPay = isZelle || isCashApp;
  const isPreorder = !!paymentDetails?.is_preorder;
  const isPickup = paymentDetails?.delivery_method === 'local_pickup' || isCash;

  const payHandle = isZelle ? (settings?.zelle_handle || '') : (settings?.cashapp_handle || '');
  const payQrUrl = isZelle ? (settings?.zelle_qr_url || '') : (settings?.cashapp_qr_url || '');
  const payNote = isZelle ? (settings?.zelle_note || '') : (settings?.cashapp_note || '');
  const payRecipient = isZelle ? (settings?.zelle_recipient_name || '') : '';
  const payLabel = isZelle ? 'Zelle' : 'Cash App';

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center py-16">
      <Card className="w-full max-w-lg" data-testid="checkout-success">
        <CardContent className="p-8 text-center">
          <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 ${isManualPay ? 'bg-amber-100' : 'bg-green-100'}`}>
            {isManualPay ? <Clock className="h-10 w-10 text-amber-600" /> : <CheckCircle2 className="h-10 w-10 text-green-600" />}
          </div>

          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-2">
            {isManualPay ? 'Order Reserved — One More Step' : isCash ? 'Order Placed!' : isPreorder ? 'Pre-Order Confirmed!' : 'Order Confirmed!'}
          </h1>
          <p className="text-slate-600 mb-8">
            {isManualPay
              ? `Your order is reserved and we've been notified. To complete it, send your ${payLabel} payment using the details below.`
              : isCash
              ? 'Your order is reserved. Bring cash at pickup to complete payment.'
              : isPreorder
              ? 'Thanks for your pre-order. We’ll ship in-stock items now and notify you when remaining items are ready.'
              : 'Thank you for your order. Your items are being prepared.'}
          </p>

          {/* Manual-payment (Zelle / Cash App) instructions — shown ONLY after
              the order exists, so customers pay AFTER we have their order. */}
          {isManualPay && (
            <div className="border-2 border-amber-300 rounded-xl p-4 mb-6 text-left bg-amber-50/50" data-testid="manual-pay-instructions">
              <h2 className="font-heading font-semibold text-slate-900 mb-3 text-center">Send your {payLabel} payment</h2>
              {payQrUrl && (
                <div className="flex flex-col items-center bg-white rounded-lg p-4 mb-3">
                  <img
                    src={`${API_URL}${payQrUrl}`}
                    alt={`${payLabel} QR code`}
                    className="w-44 h-44 object-contain rounded-lg bg-white"
                    data-testid="manual-pay-qr"
                  />
                  <span className="mt-2 text-xs text-slate-500 text-center">Open your {isZelle ? 'bank' : 'Cash App'} app and scan this QR{payHandle ? ' — or use the handle below' : ''}.</span>
                </div>
              )}
              {payHandle && (
                <div className="rounded-lg bg-white border border-slate-200 p-3 mb-3">
                  <p className="text-xs text-slate-500">Send to</p>
                  <p className="font-mono font-semibold text-purple-700 break-all">{payHandle}</p>
                  {payRecipient && <p className="text-xs text-slate-500 mt-1">{payRecipient}</p>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div className="rounded-lg bg-white border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Exact amount</p>
                  <p className="font-bold text-purple-700 text-lg" data-testid="manual-pay-amount">
                    ${Number(paymentDetails?.total || 0).toFixed(2)}
                  </p>
                </div>
                <div className="rounded-lg bg-white border border-slate-200 p-3">
                  <p className="text-xs text-slate-500">Include this memo</p>
                  <p className="font-mono font-semibold text-amber-900 break-all" data-testid="manual-pay-memo">{paymentDetails?.order_id}</p>
                </div>
              </div>
              {payNote && <p className="text-xs text-slate-600">{payNote}</p>}
              <p className="text-xs text-amber-800 mt-2 font-medium">Once we receive your payment, we’ll confirm your order and email your tracking. A copy of these instructions is in your email.</p>
            </div>
          )}

          {paymentDetails && (
            <div className="bg-slate-50 rounded-lg p-4 mb-6 text-left">
              {paymentDetails.order_id && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">Order ID</span>
                  <span className="font-mono font-semibold text-slate-800" data-testid="order-id">{paymentDetails.order_id}</span>
                </div>
              )}
              {(paymentDetails.total !== undefined && paymentDetails.total !== null) && (
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-600">{(isCash || isManualPay) ? 'Amount Due' : 'Amount Paid'}</span>
                  <span className="font-semibold">
                    ${Number(paymentDetails.total).toFixed(2)} {(paymentDetails.currency || 'USD').toUpperCase()}
                  </span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Status</span>
                <span className={`font-semibold capitalize ${(isCash || isManualPay) ? 'text-amber-600' : 'text-green-600'}`}>
                  {paymentDetails.payment_status || 'paid'}
                </span>
              </div>
              {isPickup && (paymentDetails.pickup_date || paymentDetails.pickup_time) && (
                <div className="flex justify-between text-sm mt-2 pt-2 border-t border-slate-200">
                  <span className="text-slate-600">Pickup</span>
                  <span className="font-semibold text-slate-800">
                    {paymentDetails.pickup_date} {paymentDetails.pickup_time}
                  </span>
                </div>
              )}
            </div>
          )}

          <div className="bg-teal-50 rounded-lg p-4 mb-8">
            <div className="flex items-center justify-center gap-3">
              <Package className="h-5 w-5 text-teal-700" />
              <span className="text-sm text-teal-800 font-medium">A confirmation email has been sent</span>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/dashboard" data-testid="view-order-details-btn">
              <Button className="bg-teal-700 hover:bg-teal-800 text-white w-full sm:w-auto">
                View Order Details
              </Button>
            </Link>
            <Link to="/products">
              <Button variant="outline" className="w-full sm:w-auto" data-testid="continue-shopping-btn">
                Continue Shopping
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
            <Link to="/">
              <Button variant="ghost" className="w-full sm:w-auto" data-testid="back-home-btn">
                Home
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CheckoutSuccessPage;
